import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Users, Heart, UserX, ArrowRight, Loader2, Clock, ChevronDown, Ban,
  MessageSquare, MapPin, Drumstick, Skull, Sparkles, Copy,
} from 'lucide-react';
import { serverStore } from '../../stores/server.store';
import { userStore } from '../../stores/user.store';
import { apiFetch } from '../../utils/api';
import { Card } from '../design/Card';
import { Badge } from '../design/Badge';
import { Button } from '../design/Button';
import { Input } from '../design/Input';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import { dimensionMeta, type LivePlayer } from './PlayerList';
import { PlayerAvatar } from './PlayerAvatar';
import { can } from '@/utils/permissions';

const MAX_ROWS = 6;

/**
 * Spieler-Zusammenfassung fuer die Server-Uebersicht: kompakte Reihen,
 * per Klick ausklappbar mit den wichtigsten Infos (Position, Session,
 * Tode, XP) und Sofort-Aktionen (Nachricht, Kick, Ban). Alles Weitere
 * (Gamemode, TP, OP, Bekannte/Gebannte) lebt im Spieler-Tab.
 */
export const PlayerSummaryCard = observer(() => {
  const { activeServer } = serverStore;
  const navigate = useNavigate();
  const canModerate = can(userStore.currentUser, 'can_moderate_server');

  const [live, setLive] = useState<LivePlayer[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  const fetchLive = useCallback(async () => {
    if (!activeServer || activeServer.status !== 'online') return;
    try {
      const res = await apiFetch(`/api/servers/${activeServer.id}/players/live`);
      const data = await res.json();
      if (data.success) setLive(data.players);
    } catch { /* naechster Poll */ }
    setLoading(false);
  }, [activeServer?.id, activeServer?.status]);

  useEffect(() => {
    setLoading(true);
    fetchLive();
    const t = setInterval(fetchLive, 15000);
    return () => clearInterval(t);
  }, [fetchLive]);

  if (!activeServer || activeServer.status !== 'online') return null;

  const playerCount = serverStore.serverRuntimeDetails.get(activeServer.id)?.playerCount;
  // Kein erfundenes "/20", solange die echte Maximalzahl noch nicht geladen ist.
  const max = playerCount?.max;
  const isSteam = String(activeServer.type || '').startsWith('steam');

  const doAction = async (action: string, name: string) => {
    setBusy(`${action}-${name}`);
    const res = await serverStore.executePlayerAction(activeServer.id, action, name, msg);
    if (res.success) {
      toast.success(action === 'whisper' ? `Nachricht an ${name} gesendet` : `${action} für ${name} ausgeführt`);
      if (action !== 'whisper') fetchLive();
      setMsg('');
    } else {
      toast.error(res.message || 'Aktion fehlgeschlagen');
    }
    setBusy(null);
  };

  const sessionText = (p: LivePlayer) => {
    if (!p.sessionStartedAt) return null;
    const min = Math.floor((Date.now() - p.sessionStartedAt) / 60000);
    return min < 60 ? `${min}m` : `${Math.floor(min / 60)}h ${min % 60}m`;
  };

  const shown = live.slice(0, MAX_ROWS);

  return (
    <Card className="flex flex-col h-full" noPadding>
      <div className="bg-white/5 px-5 py-3.5 border-b border-line flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-blue-400" />
          <span className="font-bold text-white">Spieler</span>
          <Badge variant="default" className="text-xs px-2 py-0.5">{max ? `${live.length}/${max}` : live.length}</Badge>
        </div>
        <button
          onClick={() => navigate(`/server/${activeServer.id}/players`)}
          className="text-xs text-fg-muted hover:text-white flex items-center gap-1 transition-colors font-medium"
        >
          Alle Details <ArrowRight size={12} />
        </button>
      </div>

      <div className="p-3 flex-1 overflow-y-auto max-h-[420px] custom-scrollbar">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={22} className="animate-spin text-fg-muted" />
          </div>
        ) : live.length === 0 ? (
          <div className="text-center py-8 text-fg-muted text-sm flex flex-col items-center">
            <Users size={26} className="mb-2 opacity-20" />
            Keine Spieler online
          </div>
        ) : (
          <div className="space-y-1.5">
            {shown.map((p) => {
              const dim = dimensionMeta(p.dimension);
              const session = sessionText(p);
              const isOpen = expanded === p.name;
              return (
                <div key={p.name} className={cn('rounded-lg border transition-colors', isOpen ? 'border-line-strong bg-black/25' : 'border-transparent hover:bg-white/5')}>
                  {/* Kompakte Reihe */}
                  <div
                    className="flex items-center gap-3 px-2 py-1.5 cursor-pointer"
                    onClick={() => setExpanded(isOpen ? null : p.name)}
                  >
                    <PlayerAvatar name={p.name} size={28} steam={isSteam} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-white truncate">{p.name}</span>
                        {dim && <span className={cn('text-[8px] px-1 py-px rounded border font-bold uppercase shrink-0', dim.cls)}>{dim.label}</span>}
                      </div>
                      <div className="flex items-center gap-3 mt-0.5">
                        {p.health !== null && (
                          <span className="flex items-center gap-1 text-[10px] text-fg-muted">
                            <Heart size={9} className={p.health <= 6 ? 'text-red-400' : 'text-green-400'} />
                            {Math.round(p.health)} HP
                          </span>
                        )}
                        {p.food !== null && (
                          <span className="flex items-center gap-1 text-[10px] text-fg-muted">
                            <Drumstick size={9} className="text-orange-400" /> {Math.round(p.food)}/20
                          </span>
                        )}
                        {session && (
                          <span className="flex items-center gap-1 text-[10px] text-fg-muted">
                            <Clock size={9} /> {session}
                          </span>
                        )}
                      </div>
                    </div>
                    <ChevronDown size={14} className={cn('text-fg-muted transition-transform shrink-0', isOpen && 'rotate-180')} />
                  </div>

                  {/* Ausgeklappt: Infos auf einen Blick + wichtigste Aktionen */}
                  {isOpen && (
                    <div className="px-2.5 pb-2.5 space-y-2 animate-in slide-in-from-top-1 duration-150">
                      {(p.pos || typeof p.deaths === 'number' || p.xpLevel !== null) && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-fg-muted bg-black/25 rounded-md px-2.5 py-1.5 border border-line">
                        {p.pos && (
                          <button
                            className="flex items-center gap-1 hover:text-blue-400 transition-colors font-mono"
                            onClick={() => { navigator.clipboard.writeText(`${p.pos!.x} ${p.pos!.y} ${p.pos!.z}`); toast.success('Koordinaten kopiert'); }}
                            title="Koordinaten kopieren"
                          >
                            <MapPin size={10} className="text-blue-400" /> {p.pos.x} / {p.pos.y} / {p.pos.z} <Copy size={9} className="opacity-50" />
                          </button>
                        )}
                        {typeof p.deaths === 'number' && (
                          <span className="flex items-center gap-1"><Skull size={10} /> {p.deaths} Tode</span>
                        )}
                        {p.xpLevel !== null && (
                          <span className="flex items-center gap-1"><Sparkles size={10} className="text-yellow-400" /> Lvl {Math.round(p.xpLevel)}</span>
                        )}
                      </div>
                      )}

                      {/* Nachricht/Kick/Ban sind Minecraft-Befehle (Name-basiert) - ARK braucht
                          dafuer die Steam-ID, daher bei Steam-Spielen der Weg ueber die Konsole. */}
                      {canModerate && isSteam && (
                        <div className="text-[11px] text-fg-muted bg-black/25 rounded-md px-2.5 py-1.5 border border-line">
                          Kick/Ban bei diesem Spiel über die Konsole: <code className="text-fg">ListPlayers</code> zeigt die Steam-ID,
                          dann <code className="text-fg">KickPlayer &lt;ID&gt;</code> bzw. <code className="text-fg">BanPlayer &lt;ID&gt;</code>.
                        </div>
                      )}

                      {canModerate && !isSteam && (
                        <>
                          <div className="flex items-center gap-1.5">
                            <Input
                              placeholder="Schnelle Nachricht…"
                              value={msg}
                              onChange={(e) => setMsg(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && msg.trim() && doAction('whisper', p.name)}
                              className="flex-1 h-8 text-xs"
                            />
                            <Button
                              size="sm"
                              variant="primary"
                              className="h-8 px-2.5"
                              disabled={!msg.trim() || busy === `whisper-${p.name}`}
                              onClick={() => doAction('whisper', p.name)}
                            >
                              <MessageSquare size={13} />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-1.5">
                            <Button
                              size="sm"
                              variant="danger"
                              className="h-8 justify-center text-xs"
                              disabled={!!busy}
                              onClick={() => doAction('kick', p.name)}
                            >
                              {busy === `kick-${p.name}` ? <Loader2 size={13} className="animate-spin" /> : <><UserX size={13} className="mr-1.5" /> Kick</>}
                            </Button>
                            <Button
                              size="sm"
                              variant="danger"
                              className="h-8 justify-center text-xs border-red-500/50 bg-red-500/10 hover:bg-red-500/20 text-red-500"
                              disabled={!!busy}
                              onClick={() => doAction('ban', p.name)}
                            >
                              {busy === `ban-${p.name}` ? <Loader2 size={13} className="animate-spin" /> : <><Ban size={13} className="mr-1.5" /> Ban</>}
                            </Button>
                          </div>
                          <button
                            onClick={() => navigate(`/server/${activeServer.id}/players`)}
                            className="w-full text-center text-[10px] text-fg-muted hover:text-white transition-colors pt-0.5"
                          >
                            Gamemode, TP, OP & mehr im Spieler-Tab →
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {live.length > MAX_ROWS && (
              <button
                onClick={() => navigate(`/server/${activeServer.id}/players`)}
                className="w-full text-center text-xs text-fg-muted hover:text-white py-1.5 transition-colors"
              >
                +{live.length - MAX_ROWS} weitere anzeigen
              </button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
});
